import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from 'src/components/ui/button';
import { Input } from 'src/components/ui/input';
import { Alert as ErrorAlert, AlertDescription } from 'src/components/ui/alert';
import { Alert } from 'src/components/common/Alert';
import { Eye, EyeOff } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from 'src/components/ui/tooltip';
import { AuthService } from 'src/services/api/AuthService';
import loginImg from 'src/assets/images/login_img.png';

const ResetPassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [confirmPasswordError, setConfirmPasswordError] = useState('');
  const [showPasswordTooltip, setShowPasswordTooltip] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [token, setToken] = useState('');

  useEffect(() => {
    const tokenFromUrl = searchParams.get('token');
    if (!tokenFromUrl) {
      setError('Invalid reset link. Token is missing.');
      return;
    }
    setToken(tokenFromUrl);
  }, [searchParams]);

  const validatePassword = (value) => {
    const hasMinLength = value.length >= 8;
    const hasLowerCase = /[a-z]/.test(value);
    const hasUpperCase = /[A-Z]/.test(value);
    const hasNumber = /\d/.test(value);
    const hasSpecialChar = /[@$!%*?&]/.test(value);
    return (
      hasMinLength &&
      hasLowerCase &&
      hasUpperCase &&
      hasNumber &&
      hasSpecialChar
    );
  };

  const handlePasswordChange = (e) => {
    setPassword(e.target.value);
    setPasswordError('');
    setError('');
  };

  const handleConfirmPasswordChange = (e) => {
    setConfirmPassword(e.target.value);
    setConfirmPasswordError('');
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    let valid = true;
    setError('');
    setPasswordError('');
    setConfirmPasswordError('');

    if (!password) {
      setPasswordError('New password is required');
      valid = false;
    } else if (!validatePassword(password)) {
      setPasswordError(
        'Password must be at least 8 characters, include uppercase, lowercase, number, and special character.'
      );
      valid = false;
    }
    if (!confirmPassword) {
      setConfirmPasswordError('Please confirm your new password');
      valid = false;
    } else if (password !== confirmPassword) {
      setConfirmPasswordError('Passwords do not match');
      valid = false;
    }
    if (!valid) return;

    setIsLoading(true);
    try {
      await AuthService.resetPassword({
        token,
        new_password: password,
        confirm_password: confirmPassword,
      });

      setShowSuccessAlert(true);
      setPassword('');
      setConfirmPassword('');
    } catch (err) {
      const errorMessage =
        'An error occurred while resetting password. Please try again.';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAlertClose = () => {
    setShowSuccessAlert(false);
    navigate('/login');
  };

  if (!token && error) {
    return (
      <div className="h-screen bg-muted flex overflow-hidden">
        <div className="w-full flex justify-center items-center p-6">
          <div className="bg-card p-8 rounded-lg shadow-md dark:border dark:border-border w-full max-w-md">
            <div className="text-center">
              <h2 className="text-2xl font-bold mb-4 text-red-600 dark:text-red-400">
                Invalid Reset Link
              </h2>
              <p className="text-muted-foreground mb-6">{error}</p>
              <Button onClick={() => navigate('/login')} className="w-full">
                Back to Login
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen bg-gray-100 flex overflow-hidden">
      {/* Left Column - Image */}
      <div className="hidden md:block w-[68%] relative">
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url(${loginImg})`,
            opacity: '0.25',
          }}
        />
        <div className="absolute inset-0 bg-black bg-opacity-30" />
        <div className="relative z-10 h-full flex flex-col justify-center px-16">
          <h1 className="text-4xl font-bold mb-4 ahDarkBlueText dark:text-primary">
            AHEDGE People Services
          </h1>
          <p className="font-medium text-gray-800 dark:text-gray-300 px-8">
            Reset your password to regain access to your account.
          </p>
        </div>
      </div>

      {/* Right Column - Form */}
      <div className="w-full md:w-[32%] flex justify-center items-center p-6 overflow-y-auto">
        <div className="w-full max-w-lg">
          <h2 className="text-2xl font-bold mb-8 text-foreground-strong">
            Set New Password
          </h2>

          {error && (
            <ErrorAlert variant="destructive" className="mb-4">
              <AlertDescription>{error}</AlertDescription>
            </ErrorAlert>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-4">
              <TooltipProvider>
                <Tooltip delayDuration={0} open={showPasswordTooltip}>
                  <TooltipTrigger asChild>
                    <div className="relative">
                      <Input
                        type={showPassword ? 'text' : 'password'}
                        placeholder="New Password"
                        value={password}
                        onChange={handlePasswordChange}
                        onFocus={() => setShowPasswordTooltip(true)}
                        onBlur={() => setShowPasswordTooltip(false)}
                        className={`h-12 ${passwordError ? 'border-red-500' : ''}`}
                        disabled={isLoading}
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-gray-700 dark:hover:text-muted-foreground"
                        tabIndex={-1}
                      >
                        {showPassword ? (
                          <EyeOff className="h-4 w-4" />
                        ) : (
                          <Eye className="h-4 w-4" />
                        )}
                      </button>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent side="right" className="p-2">
                    <div className="text-sm text-left">
                      Password must contain:
                      <div className="space-y-1 mt-1">
                        <div>• At least 8 characters</div>
                        <div>• One uppercase letter</div>
                        <div>• One lowercase letter</div>
                        <div>• One number</div>
                        <div>• One special character (@$!%*?&)</div>
                      </div>
                    </div>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
              {passwordError && (
                <p className="text-sm text-red-500 mt-1 text-left">
                  {passwordError}
                </p>
              )}

              <div className="relative">
                <Input
                  type={showConfirmPassword ? 'text' : 'password'}
                  placeholder="Confirm New Password"
                  value={confirmPassword}
                  onChange={handleConfirmPasswordChange}
                  className={`h-12 ${confirmPasswordError ? 'border-red-500' : ''}`}
                  disabled={isLoading}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  tabIndex={-1}
                >
                  {showConfirmPassword ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </button>
              </div>
              {confirmPasswordError && (
                <p className="text-sm text-red-500 mt-1 text-left">
                  {confirmPasswordError}
                </p>
              )}

              <Button
                type="submit"
                className="w-full h-12 text-base"
                disabled={isLoading}
              >
                {isLoading ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin mr-2 h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                    Resetting Password...
                  </div>
                ) : (
                  'Reset Password'
                )}
              </Button>
            </div>
          </form>
        </div>
      </div>

      <Alert
        isOpen={showSuccessAlert}
        onClose={handleAlertClose}
        title="Success"
        description="Your password has been reset successfully. Please login with your new password."
        actionText="OK"
        onContinue={handleAlertClose}
      />
    </div>
  );
};

export default ResetPassword;
